import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as CancelIcon } from '@lifesize/ux-assets/vectors/svgIcons/cancel.svg';
import intl from 'react-intl-universal';
import { makeStyles, Box, Tab, Tabs, Typography, SwipeableDrawer } from '@material-ui/core';
import { media } from '@lifesize/clients.sdk';
import { theme } from 'theme';
import isEqual from 'lodash/isEqual';
import PSTNSettings from './PSTNSettings';
import { writeToLocalStorage } from 'utils/localStorage/mediaSettings';
import { getConstraints, MediaConfig, audioOutputOptions, AudioType } from 'utils/deviceUtils';
import { getExtension, isExtensionValid } from 'utils/guest-utils';
import { Extension } from 'constants/Guest';
import { WEB_CONFERENCE_DISABLED, WEB_CONFERENCE_ENABLED } from 'constants/Call';
import { set as updateSettings } from 'reducers/mediaSettingsSlice';
import { RootState } from 'store/reducers';
import CameraDropdown from 'components/AvSettings/CameraDropdown';
import ResolutionDropdown from 'components/AvSettings/ResolutionDropdown';
import MicrophoneDropdown from 'components/AvSettings/MicrophoneDropdown';
import SpeakerDropdown from 'components/AvSettings/SpeakerDropdown';
import { getPSTNData, PSTNObject, getPstnOptions } from 'utils/pstn';
import { useMount } from 'hooks/useMount';

interface Props {
  inCall?: boolean;
  open: boolean;
  toggleDrawer: (toggle: boolean) => void;
}

const AudioVideoSettings = (props: Props): React.ComponentElement<Props, any> => {
  const { open, toggleDrawer } = props;
  const classes = useStyles({});
  const dispatch = useDispatch();
  const drawerDirection = 'left';
  const [extension, setExtension] = useState<Extension>();
  const [pstnNumbers, setPstnNumbers] = useState<PSTNObject>();
  const mediaSettings = useSelector((state: RootState) => state?.app?.mediaSettings);
  const hasPSTN = pstnNumbers?.allNumbers?.length || pstnNumbers?.customNumbers?.length;
  const isMounted = useMount();

  useEffect(() => {
    const fetchExtension = async () => {
      const extensionFromUrl = getExtension();
      const fetchedExtension = await isExtensionValid(extensionFromUrl);
      if (isMounted.current) {
        setExtension(fetchedExtension);
      }
    };

    if (!extension) {
      fetchExtension();
    }
  }, [extension, isMounted]);

  useEffect(() => {
    const getPstnNumbers = async () => {
      if (extension?.groupUUID && !pstnNumbers) {
        const result = await getPSTNData(extension.groupUUID);
        if (isMounted.current) {
          setPstnNumbers(result?.pstnNumbers);
        }
      }
    };

    if (open) {
      getPstnNumbers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extension, open, isMounted]);

  const handleConfigChange = useCallback(
    async (key: keyof MediaConfig, value: string) => {
      const newValues: MediaConfig = { ...mediaSettings, [key]: value };

      // mute/unmute audio with phone(pstn)/computer change
      if (key === 'audio') {
        if (value === AudioType.computer) {
          media.setAudioMute(false);
          newValues.localAudioMuted = false;
          dispatch({ type: WEB_CONFERENCE_DISABLED });
        } else {
          // 'phone'
          // RemoteStream video component will automatically mute when 'phone' is selected
          media.setAudioMute(true);
          newValues.localAudioMuted = true;
          dispatch({ type: WEB_CONFERENCE_ENABLED });
        }
      }

      if (key === 'localVideoMuted') {
        media.setVideoMute(value);
      }

      // persist the values
      writeToLocalStorage(newValues);
      // set the global state
      dispatch(updateSettings(newValues));

      if (['speakers', 'localVideoMuted', 'localAudioMuted'].indexOf(key) > -1) return;

      const oldConstraints = getConstraints(mediaSettings);
      const newConstraints = getConstraints(newValues);
      if (!isEqual(oldConstraints, newConstraints)) {
        const newMediaSettings = { defaultConstraints: newConstraints };
        await media.configure(newMediaSettings);
      }
    },
    [mediaSettings, dispatch]
  );

  const handleAudioTabChange = (event: any, newValue: number) => {
    handleConfigChange('audio', audioOutputOptions[newValue]);
  };

  return (
    <SwipeableDrawer
      anchor={drawerDirection}
      onClose={() => toggleDrawer(false)}
      onOpen={() => {}}
      open={open}
      classes={{ paper: classes.drawerPaper }}
    >
      <button className={classes.closeButton} onClick={() => toggleDrawer(false)}>
        <CancelIcon data-testid="cancelIcon" />
      </button>
      <Box className={classes.container}>
        <Box className={classes.scrollContainer} p={2}>
          <Box className={classes.form} data-testid="avSettingsDivision">
            <Box pb={1} className={classes.heading}>
              <Typography component="div" className={classes.headingTitle} data-testid="videoSettingsHeader">
                {intl.get('videoSettings')}
              </Typography>
            </Box>

            <Box pb={1} className={classes.settingsSelector}>
              <CameraDropdown handleConfigChange={handleConfigChange} wide={true} />
              <ResolutionDropdown handleConfigChange={handleConfigChange} wide={true} />
            </Box>

            <Box pb={1} mt={2} className={classes.heading}>
              <Typography component="div" className={classes.headingTitle} data-testid="audioSettingsHeader">
                {intl.get('audioSettings')}
              </Typography>
            </Box>

            {!!hasPSTN && (
              <Tabs
                aria-label={intl.get('audioLabel')}
                indicatorColor="primary"
                onChange={handleAudioTabChange}
                textColor="primary"
                value={audioOutputOptions.findIndex((opt) => opt === mediaSettings.audio)}
                variant="fullWidth"
              >
                <Tab
                  className={classes.tabStyles}
                  data-testid="computer-audio-tab"
                  label={intl.get('deviceAudioLabel')}
                />
                <Tab className={classes.tabStyles} data-testid="phone-audio-tab" label={intl.get('phoneAudioLabel')} />
              </Tabs>
            )}

            <Box pb={1} className={classes.settingsSelector}>
              {(mediaSettings.audio === 'computer' || !hasPSTN) && (
                <>
                  <MicrophoneDropdown handleConfigChange={handleConfigChange} wide={true} />
                  <SpeakerDropdown handleConfigChange={handleConfigChange} wide={true} />
                </>
              )}

              {mediaSettings.audio === 'phone' && hasPSTN && extension?.value && (
                <PSTNSettings extension={extension.value} pstnNumbers={getPstnOptions(pstnNumbers)} />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </SwipeableDrawer>
  );
};

const useStyles = makeStyles(() => ({
  drawerPaper: {
    backgroundColor: '#f5f7fa',
    maxWidth: '100%',
    width: 400,
    position: 'relative',
    overflowY: 'auto'
  },
  container: {
    color: '#616e7c',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    position: 'relative',
    marginTop: theme.spacing(4)
  },
  heading: {
    alignItems: 'flex-start',
    borderBottom: `1px solid #cbd2d9`,
    display: 'flex',
    flex: '0 0 auto',
    justifyContent: 'center'
  },
  headingTitle: {
    alignItems: 'center',
    display: 'flex',
    flex: '1 1 auto',
    fontSize: '1.3rem',
    fontWeight: 500
  },
  scrollContainer: {
    height: '100%',
    width: '100%'
  },
  form: {
    display: 'flex',
    flex: '1 0 auto',
    flexDirection: 'column',
    minHeight: '100%',
    width: '100%'
  },
  closeButton: {
    position: 'absolute',
    zIndex: 1,
    right: 16,
    top: 16,
    outline: 'none',
    border: 'none',
    height: 40,
    width: 40,
    padding: 0,
    color: '#9aa5b1',
    borderRadius: 20,
    transition:
      'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.15)'
    }
  },
  settingsSelector: {
    '& section': {
      marginTop: 15
    }
  },
  tabStyles: {
    fontSize: '1.1rem',
    textTransform: 'none',
    fontWeight: 400
  }
}));

export default AudioVideoSettings;
